import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Pencil, Trash2, Warehouse, MapPin } from 'lucide-react';
import {
  apiListWarehouses, apiCreateWarehouse, apiUpdateWarehouse, apiDeleteWarehouse,
  apiListLocations, apiCreateLocation, apiUpdateLocation, apiDeleteLocation
} from '../../erpApi';
import { toast } from 'sonner';

const WarehouseForm = ({ initial, onSubmit, onCancel }) => {
  const [f, setF] = useState(initial || { code: '', name: '', address: '', notes: '', active: true });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3">
      <div className="space-y-1.5"><Label>Codice *</Label><Input data-testid="wh-code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className="font-mono uppercase" required /></div>
      <div className="space-y-1.5"><Label>Nome *</Label><Input data-testid="wh-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
      <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
      <div className="flex items-center justify-between p-2 rounded border border-slate-200"><Label className="mb-0">Attivo</Label><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></div>
      <DialogFooter><Button type="button" variant="outline" onClick={onCancel}>Annulla</Button><Button data-testid="wh-submit" type="submit" className="bg-blue-700 hover:bg-blue-800">{initial ? 'Salva' : 'Crea'}</Button></DialogFooter>
    </form>
  );
};

const LocationDialog = ({ warehouse, onClose, onReload }) => {
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ code: '', description: '', active: true });
  const [editing, setEditing] = useState(null);
  const reload = async () => setLocations(await apiListLocations(warehouse.id));
  useEffect(() => { reload(); }, [warehouse.id]); // eslint-disable-line
  const add = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return toast.error('Codice obbligatorio');
    try {
      if (editing) await apiUpdateLocation(editing.id, form);
      else await apiCreateLocation({ ...form, warehouse_id: warehouse.id });
      setForm({ code: '', description: '', active: true });
      setEditing(null);
      reload(); onReload?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };
  const del = async (loc) => {
    if (!window.confirm(`Eliminare ${loc.code}?`)) return;
    await apiDeleteLocation(loc.id); reload();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Ubicazioni di {warehouse.code} — {warehouse.name}</DialogTitle><DialogDescription>Scaffali / corsie / ripiani interni al magazzino.</DialogDescription></DialogHeader>
        <form onSubmit={add} className="flex items-end gap-2 pb-3 border-b">
          <div className="flex-1 space-y-1"><Label className="text-xs">Codice ubicazione</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="A-01-03" className="font-mono uppercase h-8" required /></div>
          <div className="flex-1 space-y-1"><Label className="text-xs">Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Corsia A, scaffale 01, ripiano 03" className="h-8" /></div>
          <Button type="submit" className="h-8 bg-blue-700 hover:bg-blue-800">{editing ? 'Salva' : <Plus className="w-3.5 h-3.5" />}</Button>
        </form>
        {locations.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">Nessuna ubicazione</div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {locations.map(loc => (
              <div key={loc.id} className="flex items-center justify-between text-sm py-1.5 px-2 hover:bg-slate-50 rounded">
                <div><span className="font-mono font-medium text-slate-900">{loc.code}</span> <span className="text-slate-500">— {loc.description || '—'}</span></div>
                <div>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setEditing(loc); setForm(loc); }}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600" onClick={() => del(loc)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const WarehousesManager = () => {
  const [list, setList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openLocFor, setOpenLocFor] = useState(null);
  const reload = async () => setList(await apiListWarehouses());
  useEffect(() => { reload(); }, []); // eslint-disable-line
  const handleSubmit = async (data) => {
    try {
      if (editing) await apiUpdateWarehouse(editing.id, data); else await apiCreateWarehouse(data);
      toast.success('OK'); setDialogOpen(false); setEditing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  const del = async (w) => {
    if (!window.confirm(`Eliminare ${w.code}?`)) return;
    try { await apiDeleteWarehouse(w.id); toast.success('Eliminato'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };
  return (
    <div className="space-y-3" data-testid="warehouses-manager">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">Magazzini e ubicazioni interne</div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><Button data-testid="wh-new" onClick={() => setDialogOpen(true)} className="bg-blue-700 hover:bg-blue-800 gap-2 h-9"><Plus className="w-4 h-4" />Nuovo magazzino</Button></DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>{editing ? 'Modifica magazzino' : 'Nuovo magazzino'}</DialogTitle><DialogDescription>Definisci codice e nome del magazzino.</DialogDescription></DialogHeader>
            <WarehouseForm initial={editing} onSubmit={handleSubmit} onCancel={() => { setDialogOpen(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300"><Warehouse className="w-10 h-10 text-slate-300 mx-auto mb-2" /><div className="text-sm">Nessun magazzino</div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map(w => (
            <div key={w.id} className={`bg-white border border-slate-200 rounded-xl p-4 ${!w.active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-xs text-slate-500">{w.code}</div>
                  <div className="font-semibold text-slate-900">{w.name}</div>
                  {w.address && <div className="text-xs text-slate-500 mt-0.5">{w.address}</div>}
                </div>
                <div className="flex items-center">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(w); setDialogOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => del(w)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
              <Button variant="outline" size="sm" className="mt-3 w-full gap-2 text-xs h-8" onClick={() => setOpenLocFor(w)}>
                <MapPin className="w-3 h-3" /> Gestisci ubicazioni
              </Button>
            </div>
          ))}
        </div>
      )}

      {openLocFor && <LocationDialog warehouse={openLocFor} onClose={() => setOpenLocFor(null)} />}
    </div>
  );
};

export default WarehousesManager;
